/* eslint-env browser */
// Maintenance tab — admin one-shot tools and resumable backfill runners.
// (Wave 3: restyled only — cards, tokens, inline messages, status chips.
// Batch sizes, URLs, chunking and every call are unchanged.)
//
// Moved here from the SecurityDashboard header row (logic unchanged; Flat-Rate
// All uses the drivers-only read added to common/ in the same change set):
//   * Flat-Rate All             — bulk-enrol drivers on app >= MIN_FLAT_RATE_APP_VERSION
//   * Fill Vehicle Placeholders — VehiclePlaceholderDialog
//   * Rebuild Customer Index    — backfillCustomerIndex via the Hosting host
//
// New here — two resumable runners that call the DIRECT Cloud Function URL
// (https://us-central1-<projectId>.cloudfunctions.net/<name>): Firebase
// Hosting rewrites 504 at 60 s, so a long backfill must bypass Hosting.
//   * backfillReportRollups — /report_rollups parity check (dry run) or rebuild
//   * backfillTripdateMs    — tripdateMs count-parity (dry run) or stamping
//   * seedDemandHistory     — Hot zones demand-map history seed (28 days by
//                             default; one Addis day per query, resumable by date)
// Each call carries a maxSeconds budget well under browser idle limits and
// this tab loops client-side on the server's resume token (nextMonth /
// nextCursor) until done === true. The admin ID token is fetched per call
// (getIdToken() refreshes it transparently on long runs) and never rendered.
//
// Switching tabs unmounts this component: the loop stops after the call in
// flight and the tripdateMs cursor survives in localStorage so the job can be
// resumed without re-reading the whole /bookings tree.

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { update } from 'firebase/database';
import { FirebaseContext, api } from 'common';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import StorageIcon from '@mui/icons-material/Storage';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VehiclePlaceholderDialog from './VehiclePlaceholderDialog';
import { useTranslation } from 'react-i18next';
import { confirmDialog, InlineMessage, EmptyState } from '../../components/feedback';
import { SectionCard, FieldLabel, StatusChip, formatNumber } from '../../components/modern';
import { tokens as c } from '../../theme';

const nowrapSx = { whiteSpace: 'nowrap' };
const descSx = { color: c.INK_2, mt: 0.5, mb: 2 };
const progressSx = { fontVariantNumeric: 'tabular-nums', fontSize: 13.5, color: c.INK_2, mb: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
const cellSx = { py: 0.75, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const headSx = { ...cellSx, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontWeight: 600, fontSize: 12.5 };
// One labelled input of a runner (the label above the box).
const LabelledField = ({ id, label, sx, ...props }) => (
  <Box sx={sx}>
    <FieldLabel htmlFor={id}>{label}</FieldLabel>
    <TextField id={id} size="small" fullWidth {...props} />
  </Box>
);

// Bulk flat-rate enrollment targets drivers whose installed app is at
// least this version — older builds lack the flat-rate wallet/accept
// logic, so switching them would change their billing with no UI to
// show it.
const MIN_FLAT_RATE_APP_VERSION = '2.0.56';

// Flat-Rate All writes this many drivers per update(). RTDB rejects a single
// write that would trigger more than 1000 functions; ~4 fire per /users/{uid}
// (usersCacheInvalidator, maintainEligibleDrivers, maintainFlatRateDrivers,
// maintainCustomerIndex), so 100 uids is ~400 triggers per write.
const FLAT_RATE_CHUNK_SIZE = 100;

const versionGte = (v, min) => {
  const a = String(v || '').split('.').map((n) => parseInt(n, 10));
  const b = String(min).split('.').map((n) => parseInt(n, 10));
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = Number.isFinite(a[i]) ? a[i] : 0;
    const y = Number.isFinite(b[i]) ? b[i] : 0;
    if (x !== y) return x > y;
  }
  return true;
};

// ── Runner constants ──────────────────────────────────────────────────

// Region follows functions/index.js setGlobalOptions: the second label of
// databaseURL when it has four ('<id>-default-rtdb.<region>.firebasedatabase.app'),
// else us-central1.
const directFunctionRegion = (config) => {
  const parts = String((config && config.databaseURL) || '').replace(/^https?:\/\//, '').split('/')[0].split('.');
  return parts.length === 4 ? parts[1] : 'us-central1';
};
// Per-call time budgets. tripdateMs stays at 50 s (the server default);
// rollups at ~120 s — one Addis month at a time, still far below any
// browser / proxy idle timeout. The client loops on the resume token.
const TRIPDATE_MAX_SECONDS = 50;
const ROLLUPS_MAX_SECONDS = 120;
// Demand-map seed: ~0.8 MB of /bookings per day today, so 120 s covers the
// default 28 days in one call; the client loops on nextDate otherwise.
const DEMAND_SEED_MAX_SECONDS = 120;
const DEMAND_SEED_DEFAULT_DAYS = 28;
const DEMAND_SEED_MAX_DAYS = 56;
// Mirrors BACKFILL_MIN_MONTH in functions/common/reportRollups.js.
const ROLLUP_MIN_MONTH = '2025-09';
// Money fields of /report_rollups/system_monthly (everything but total_rides).
const ROLLUP_MONEY_FIELDS = ['tripCost', 'driverShare', 'customerPaid', 'convenienceFee', 'discountAmount', 'fleetadminFee', 'cancellationFee'];
const TRIPDATE_CURSOR_KEY = 'maintenance.tripdateMsCursor';

const ADDIS_OFFSET_MS = 3 * 60 * 60 * 1000;
// Zero-padded 'YYYY-MM' in Addis local time — the rollup month key.
const addisMonthKey = (ms) => {
  const d = new Date(ms + ADDIS_OFFSET_MS);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const isMonthKey = (mk) => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(mk || '').trim());

const fmtNum = (n) => formatNumber(Number(n || 0));
const fmtMoney = (n) => Number(n || 0).toFixed(2);
const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
};
const shortKey = (k) => (k && k.length > 24 ? `${k.slice(0, 12)}…${k.slice(-8)}` : (k || '—'));

const readSavedCursor = () => {
  try { return window.localStorage.getItem(TRIPDATE_CURSOR_KEY) || ''; } catch (_) { return ''; }
};
const saveCursor = (cursor) => {
  try {
    if (cursor) window.localStorage.setItem(TRIPDATE_CURSOR_KEY, cursor);
    else window.localStorage.removeItem(TRIPDATE_CURSOR_KEY);
  } catch (_) { /* per-viewer convenience only */ }
};

// GET the direct Cloud Function URL with the current admin's ID token.
// Throws on !response.ok with the server's error message; returns JSON.
const callDirectFunction = async (firebaseAuth, projectId, region, name, params) => {
  const currentUser = firebaseAuth && firebaseAuth.currentUser;
  if (!currentUser) throw new Error('Not authenticated');
  const idToken = await currentUser.getIdToken();
  const qs = new URLSearchParams();
  Object.keys(params || {}).forEach((k) => {
    const v = params[k];
    if (v === undefined || v === null || v === '') return;
    qs.set(k, String(v));
  });
  const query = qs.toString();
  const url = `https://${region}-${projectId}.cloudfunctions.net/${name}${query ? `?${query}` : ''}`;
  let response;
  try {
    response = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } });
  } catch (err) {
    // fetch rejects on network AND on a CORS refusal (origin not allowlisted
    // in fixCorsIssue) — both surface as "Failed to fetch".
    throw new Error(`${name}: ${err.message} (network or CORS — the portal origin must be allowlisted in the function)`);
  }
  const text = await response.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) { body = null; }
  if (!response.ok) {
    const msg = (body && body.error) || (text ? text.slice(0, 200) : '') || response.statusText || 'request failed';
    throw new Error(`${name}: ${msg} (HTTP ${response.status})`);
  }
  if (!body || typeof body !== 'object') throw new Error(`${name}: empty or non-JSON response`);
  return body;
};

// One dry-run month row → { rides, maxMoneyDiff, worstField, tolerance, flagged, driverMismatch, fleetMismatch }.
const summariseParityMonth = (r) => {
  const sys = (r && r.system) || {};
  const ridesComputed = Number((sys.total_rides && sys.total_rides.computed) || 0);
  const ridesStored = Number((sys.total_rides && sys.total_rides.stored) || 0);
  const tolerance = Math.max(1, 0.01 * ridesComputed);
  let maxMoneyDiff = 0;
  let worstField = null;
  ROLLUP_MONEY_FIELDS.forEach((f) => {
    const d = Math.abs(Number((sys[f] && sys[f].diff) || 0));
    if (d > maxMoneyDiff) { maxMoneyDiff = d; worstField = f; }
  });
  const ridesDiff = Math.abs(Number((sys.total_rides && sys.total_rides.diff) || 0));
  const flagged = maxMoneyDiff > tolerance || ridesDiff > tolerance;
  const drivers = (r && r.drivers) || {};
  const fleets = (r && r.fleets) || {};
  return {
    ridesComputed,
    ridesStored,
    tolerance,
    maxMoneyDiff,
    worstField,
    flagged,
    driverMismatch: Number(drivers.mismatchedRows || 0),
    driverRows: drivers.rows || { computed: 0, stored: 0 },
    fleetMismatch: Number(fleets.mismatchedRows || 0),
    fleetRows: fleets.rows || { computed: 0, stored: 0 },
  };
};

export default function MaintenanceTools() {
  const { t } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const settingsData = useSelector((state) => state.settingsdata);
  const usersdata = useSelector((state) => state.usersdata);
  const dispatch = useDispatch();
  const { auth: firebaseAuth, config, usersRef } = useContext(FirebaseContext);
  const isAdmin = auth?.profile?.usertype === 'admin';
  const projectId = (config && config.projectId) || 'donkey-drive-5cdd1';
  const region = directFunctionRegion(config);

  // Moved from SecurityDashboard.
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollProgress, setEnrollProgress] = useState(null); // null | { done, total }
  // Vehicle placeholders (one-shot backfill, see ./VehiclePlaceholderDialog).
  const [placeholderOpen, setPlaceholderOpen] = useState(false);

  // Runners. stopRef is read between calls; the call in flight completes.
  const stopRef = useRef(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [fromMonth, setFromMonth] = useState(ROLLUP_MIN_MONTH);
  const [toMonth, setToMonth] = useState(() => addisMonthKey(Date.now()));
  const [rollupsMode, setRollupsMode] = useState(null); // null | 'dry' | 'run'
  const [rollupsProgress, setRollupsProgress] = useState(null);
  const [rollupsResult, setRollupsResult] = useState(null);
  const [cursorField, setCursorField] = useState(readSavedCursor);
  const [tripdateMode, setTripdateMode] = useState(null); // null | 'dry' | 'run'
  const [tripdateProgress, setTripdateProgress] = useState(null);
  const [tripdateResult, setTripdateResult] = useState(null);
  // Hot zones demand-map seed (seedDemandHistory): days of history, mode, progress, result.
  const [seedDays, setSeedDays] = useState(String(DEMAND_SEED_DEFAULT_DAYS));
  // Resume date (YYYY-MM-DD) owned by a real seed run: set from the server's
  // nextDate after every call, cleared when done, so a stopped or failed run
  // continues where it left off instead of re-reading the first days.
  const [seedFrom, setSeedFrom] = useState('');
  const [seedMode, setSeedMode] = useState(null); // null | 'dry' | 'run'
  const [seedProgress, setSeedProgress] = useState(null);
  const [seedResult, setSeedResult] = useState(null);

  const busy = enrolling || rebuilding || rollupsMode !== null || tripdateMode !== null || seedMode !== null;
  const runFunction = (name, params) => callDirectFunction(firebaseAuth, projectId, region, name, params);
  const requestStop = () => { stopRef.current = true; setStopRequested(true); };

  // Unmount (tab switch) halts any loop after the call in flight.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { stopRef.current = true; unmountedRef.current = true; };
  }, []);

  // On-demand seed/refresh of /customer_index (the slim, paginated customer
  // projection that powers the call-center on-demand customer loading). Calls
  // the admin-gated backfillCustomerIndex Cloud Function with the current
  // admin's ID token — same pattern as the Driver Reset trigger. The nightly
  // reconcileCustomerIndex keeps it fresh; this is for an immediate rebuild.
  const handleRebuildCustomerIndex = async () => {
    setRebuilding(true);
    setRebuildResult(null);
    try {
      const currentUser = firebaseAuth && firebaseAuth.currentUser;
      if (!currentUser) {
        setRebuildResult({ error: 'Not authenticated' });
        setRebuilding(false);
        return;
      }
      const idToken = await currentUser.getIdToken();
      const settings = settingsData && settingsData.settings;
      const host = (typeof window !== 'undefined' && window.location && settings && settings.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${(config && config.projectId) || 'donkey-drive-5cdd1'}.web.app`;
      const response = await fetch(`${host}/backfillCustomerIndex`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({}),
      });
      const result = await response.json();
      if (response.ok) {
        setRebuildResult({ success: true, message: `Customer index rebuilt: ${result.total} customers (${result.withInvited} with invites, ${result.appLess} app-less) in ${result.elapsedMs} ms.` });
      } else {
        setRebuildResult({ error: result.error || 'Rebuild failed' });
      }
    } catch (error) {
      setRebuildResult({ error: error.message });
    } finally {
      setRebuilding(false);
    }
  };

  // One-shot migration: switch every driver on app >= MIN_FLAT_RATE_APP_VERSION
  // to the daily flat-rate plan. Client-side on purpose — the users list is
  // already in Redux and multi-path updates (FLAT_RATE_CHUNK_SIZE drivers
  // each) cover all drivers, so there is no Cloud Function to deploy. Each
  // /users/{uid} write fires maintainFlatRateDrivers server-side, which fills
  // /flat_rate_drivers.
  const handleBulkFlatRateEnroll = async () => {
    const settings = settingsData && settingsData.settings;
    if (!settings || settings.driverFlatRateEnabled !== true) {
      setRebuildResult({ error: 'Enable the daily flat-rate plan in Settings first (driverFlatRateEnabled).' });
      return;
    }
    // FRESH pull — the cached users list can carry appVersion values
    // hours old, which silently skipped drivers who updated their app
    // recently (e.g. a 2.0.58 driver whose cached row still said 2.0.51).
    // Drivers-only indexed read: the button never needs customers, so the
    // forced full /users refetch was pure RTDB bandwidth.
    if (typeof api.fetchDriversOnce !== 'function') {
      // The drivers-only read ships with the common/ package of this release;
      // never fall back to hours-stale cached rows for a money-affecting change.
      setRebuildResult({ error: 'fetchDriversOnce is not available in this build — deploy the matching common/ package first.' });
      return;
    }
    setEnrolling(true);
    let users = null;
    try {
      users = await dispatch(api.fetchDriversOnce());
    } catch (_) { /* fall back to cache below */ }
    setEnrolling(false);
    if (!users || users.length === 0) users = usersdata && usersdata.staticusers;
    if (!users || users.length === 0) {
      setRebuildResult({ error: 'Users not loaded — check your connection and retry.' });
      return;
    }
    const allowFleet = settings.flatRateAllowFleetDrivers === true;
    const candidates = users.filter((u) =>
      u && u.id && u.usertype === 'driver' &&
      u.commissionMode !== 'flat_daily' &&
      (!u.fleetadmin || allowFleet) &&
      versionGte(u.appVersion, MIN_FLAT_RATE_APP_VERSION)
    );
    if (candidates.length === 0) {
      setRebuildResult({ success: true, message: `No eligible drivers to switch — everyone on app ${MIN_FLAT_RATE_APP_VERSION}+ is already enrolled (or excluded as fleet/older versions).` });
      return;
    }
    const ok = await confirmDialog({
      title: t('flat_rate_enrol_confirm', { drivers: candidates.length, version: MIN_FLAT_RATE_APP_VERSION, defaultValue: 'Switch {{drivers}} driver(s) on app {{version}}+ to the daily flat-rate plan?' }),
      body: t('flat_rate_enrol_confirm_body', { defaultValue: "They stop paying per-trip commission and start being charged their car type's daily rate. Reverting is per-driver via Users → Edit User." }),
      confirmLabel: t('flat_rate_enrol_confirm_button', { defaultValue: 'Switch drivers' }),
    });
    if (!ok) return;
    stopRef.current = false;
    setStopRequested(false);
    setEnrolling(true);
    setRebuildResult(null);
    const total = candidates.length;
    const now = Date.now();
    let switched = 0;
    let stoppedEarly = false;
    setEnrollProgress({ done: 0, total });
    try {
      // Sequential chunks, one update() each: RTDB rejects a write that would
      // trigger more than 1000 functions (TOO_MANY_TRIGGERS) and ~4 functions
      // fire per /users/{uid}, so one update for every candidate fails whole
      // above ~250 drivers. Stops at the first failing chunk; chunks already
      // written stay switched, and a re-run only picks up the remaining drivers
      // (the fresh pull above excludes anyone already on flat_daily).
      for (let i = 0; i < total; i += FLAT_RATE_CHUNK_SIZE) {
        // Same contract as the other runners: a stop (or unmount / tab switch)
        // takes effect between chunks; the chunk in flight completes.
        if (stopRef.current) { stoppedEarly = true; break; }
        const chunk = candidates.slice(i, i + FLAT_RATE_CHUNK_SIZE);
        const updates = {};
        chunk.forEach((u) => {
          updates[`${u.id}/commissionMode`] = 'flat_daily';
          updates[`${u.id}/commissionModeUpdatedAt`] = now;
          updates[`${u.id}/flatRateBulkEnrolled`] = true;
        });
        await update(usersRef, updates);
        // Patch the local redux/IDB cache in place — no /users refetch
        // (that would re-download the whole tree or serve stale cache).
        chunk.forEach((u) => {
          api.patchUsersCacheUpsert(u.id, { commissionMode: 'flat_daily', commissionModeUpdatedAt: now, flatRateBulkEnrolled: true });
        });
        switched += chunk.length;
        if (!unmountedRef.current) setEnrollProgress({ done: switched, total });
      }
      if (unmountedRef.current) return;
      if (stoppedEarly) {
        setRebuildResult({
          error: `Switched ${switched}/${total} driver(s), stopped — run Flat-Rate All again for the remaining ${total - switched}.`,
        });
      } else {
        setRebuildResult({ success: true, message: `Switched ${switched} driver(s) to the daily flat-rate plan.` });
      }
    } catch (error) {
      if (unmountedRef.current) return;
      setRebuildResult({
        error: `Switched ${switched}/${total} driver(s), then stopped: ${error.message}. ` +
          `Run Flat-Rate All again to switch the remaining ${total - switched}.`,
      });
    } finally {
      if (!unmountedRef.current) {
        setEnrolling(false);
        setEnrollProgress(null);
        setStopRequested(false);
      }
    }
  };

  // ── Earnings rollups runner ─────────────────────────────────────────
  // Loops backfillReportRollups over [fromMonth, toMonth], resuming with
  // fromMonth=nextMonth until done. Dry run accumulates the per-month
  // parity report; Run overwrites the months and collects their scan stats.
  const runRollups = async (dry) => {
    const from = String(fromMonth || '').trim();
    const to = String(toMonth || '').trim();
    if (!isMonthKey(from) || !isMonthKey(to)) {
      setRollupsResult({ error: 'From / To must be YYYY-MM month keys (e.g. 2025-09).' });
      return;
    }
    if (from < ROLLUP_MIN_MONTH) {
      setRollupsResult({ error: `From must be ${ROLLUP_MIN_MONTH} or later (oldest booking month).` });
      return;
    }
    if (from > to) {
      setRollupsResult({ error: 'From month is after To month.' });
      return;
    }
    if (!dry && !(await confirmDialog({
      title: t('rollups_rebuild_confirm', { defaultValue: 'Overwrite /report_rollups months from the bookings tree?' }),
      confirmLabel: t('rollups_rebuild_confirm_button', { defaultValue: 'Overwrite' }),
      destructive: true,
    }))) return;

    const mode = dry ? 'dry' : 'run';
    stopRef.current = false;
    setStopRequested(false);
    setRollupsMode(mode);
    setRollupsResult(null);
    setRollupsProgress({ calls: 0, months: 0, lastMonth: null, elapsedMs: 0 });
    const t0 = Date.now();
    const months = {};
    let calls = 0;
    let cursor = from;
    let nextMonth = null; // set when stopped / errored before done
    try {
      let done = false;
      while (!done) {
        const res = await runFunction('backfillReportRollups', {
          dryRun: dry ? 1 : undefined,
          fromMonth: cursor,
          toMonth: to,
          maxSeconds: ROLLUPS_MAX_SECONDS,
        });
        calls += 1;
        Object.assign(months, res.months || {});
        const keys = Object.keys(months).sort();
        setRollupsProgress({ calls, months: keys.length, lastMonth: keys[keys.length - 1] || null, elapsedMs: Date.now() - t0 });
        done = res.done === true;
        if (done) break;
        if (!isMonthKey(res.nextMonth) || res.nextMonth <= cursor) {
          throw new Error(`Server returned done=false without progress (nextMonth=${res.nextMonth})`);
        }
        cursor = res.nextMonth;
        if (stopRef.current) { nextMonth = cursor; break; }
      }
      setRollupsResult({ mode, months, calls, elapsedMs: Date.now() - t0, nextMonth, stopped: nextMonth !== null });
    } catch (error) {
      setRollupsResult({ mode, months, calls, elapsedMs: Date.now() - t0, nextMonth: cursor, error: error.message });
    } finally {
      setRollupsMode(null);
      setRollupsProgress(null);
      setStopRequested(false);
    }
  };

  // ── tripdateMs runner ───────────────────────────────────────────────
  // Loops backfillTripdateMs from the cursor field (blank = tree start),
  // resuming with startAfter=nextCursor until done. The field (and
  // localStorage) always hold the latest resume cursor; '' once done.
  const runTripdate = async (dry) => {
    if (!dry && !(await confirmDialog({
      title: t('tripdate_stamp_confirm', { defaultValue: 'Stamp tripdateMs on every booking?' }),
      body: t('tripdate_stamp_confirm_body', { defaultValue: 'It runs throttled. Best run at night with portal sessions closed.' }),
      confirmLabel: t('tripdate_stamp_confirm_button', { defaultValue: 'Start stamping' }),
    }))) return;
    const mode = dry ? 'dry' : 'run';
    stopRef.current = false;
    setStopRequested(false);
    setTripdateMode(mode);
    setTripdateResult(null);
    const t0 = Date.now();
    const totals = { calls: 0, scanned: 0, stamped: 0, skipped: 0, skippedPending: 0, unparseable: 0, flushes: 0 };
    let cursor = String(cursorField || '').trim() || null;
    let done = false;
    let stopped = false;
    setTripdateProgress({ ...totals, cursor, elapsedMs: 0 });
    try {
      while (!done) {
        const res = await runFunction('backfillTripdateMs', {
          dryRun: dry ? 1 : undefined,
          startAfter: cursor || undefined,
          maxSeconds: TRIPDATE_MAX_SECONDS,
        });
        totals.calls += 1;
        ['scanned', 'stamped', 'skipped', 'skippedPending', 'unparseable', 'flushes'].forEach((k) => {
          totals[k] += Number(res[k] || 0);
        });
        const sent = cursor;
        done = res.done === true;
        cursor = done ? null : (res.nextCursor || null);
        if (!dry) {
          // Only a stamping run owns the resume cursor — a stopped dry-run
          // must not make the next Run start mid-tree.
          setCursorField(cursor || '');
          saveCursor(cursor);
        }
        setTripdateProgress({ ...totals, cursor, elapsedMs: Date.now() - t0 });
        if (done) break;
        if (!cursor || cursor === sent) throw new Error(`Server returned done=false without progress (nextCursor=${res.nextCursor})`);
        if (stopRef.current) { stopped = true; break; }
      }
      setTripdateResult({ mode, ...totals, done, stopped, nextCursor: cursor, elapsedMs: Date.now() - t0 });
    } catch (error) {
      // cursor is the last SUCCESSFUL nextCursor — the right resume point
      // (re-running the failed page is idempotent: stamped rows are skipped).
      setTripdateResult({ mode, ...totals, done: false, stopped: false, nextCursor: cursor, elapsedMs: Date.now() - t0, error: error.message });
    } finally {
      setTripdateMode(null);
      setTripdateProgress(null);
      setStopRequested(false);
    }
  };

  // ── Demand-map history seed runner ──────────────────────────────────
  // Backfills /demand_cells/hist for the last N Addis days from /bookings
  // (one tripdateMs window per day, absolute overwrite per day → idempotent,
  // today never seeded) and recomputes the absolute reference at the end.
  // Each call runs at most DEMAND_SEED_MAX_SECONDS; the tab continues from
  // the server's nextDate until done. Dry-run writes nothing.
  const seedDaysNumber = () => {
    const n = parseInt(String(seedDays).trim(), 10);
    if (!Number.isFinite(n)) return DEMAND_SEED_DEFAULT_DAYS;
    return Math.max(1, Math.min(DEMAND_SEED_MAX_DAYS, n));
  };
  const runDemandSeed = async (dry) => {
    const days = seedDaysNumber();
    if (!dry && !(await confirmDialog({
      title: t('demand_seed_confirm', { days, defaultValue: 'Seed {{days}} day(s) of demand history from /bookings?' }),
      body: t('demand_seed_confirm_body', { days, defaultValue: 'It reads about {{days}} × 1 MB, and days seeded before are overwritten. Best run at night.' }),
      confirmLabel: t('demand_seed_confirm_button', { defaultValue: 'Seed history' }),
      destructive: true,
    }))) return;
    const mode = dry ? 'dry' : 'run';
    stopRef.current = false;
    setStopRequested(false);
    setSeedMode(mode);
    setSeedResult(null);
    const t0 = Date.now();
    const totals = { calls: 0, processed: 0, scanned: 0, n: 0, h: 0, u: 0, written: 0, cleared: 0, skipped: {} };
    const isDateKey = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || '').trim());
    let cursor = isDateKey(seedFrom) ? String(seedFrom).trim() : null;
    let first = null;
    let last = null;
    let ref = null;
    let done = false;
    let stopped = false;
    setSeedProgress({ ...totals, cursor, elapsedMs: 0 });
    try {
      while (!done) {
        const res = await runFunction('seedDemandHistory', {
          days,
          from: cursor || undefined,
          dryRun: dry ? 1 : undefined,
          maxSeconds: DEMAND_SEED_MAX_SECONDS,
        });
        totals.calls += 1;
        totals.processed += Number(res.processed || 0);
        totals.scanned += Number(res.scanned || 0);
        totals.written += Number(res.written || 0);
        totals.cleared += Number(res.cleared || 0);
        const counted = res.counted || {};
        totals.n += Number(counted.n || 0);
        totals.h += Number(counted.h || 0);
        totals.u += Number(counted.u || 0);
        Object.keys(res.skipped || {}).forEach((k) => { totals.skipped[k] = (totals.skipped[k] || 0) + Number(res.skipped[k] || 0); });
        if (!first && res.from) first = res.from;
        if (res.to) last = res.to;
        // The server returns the recomputed reference as { ref, refSlots, p85, weeks }.
        const refNum = res.ref && typeof res.ref === 'object' ? res.ref.ref : res.ref;
        if (Number.isFinite(Number(refNum))) ref = Number(refNum);
        const sent = cursor;
        done = res.done === true;
        cursor = done ? null : (res.nextDate || null);
        // Only a real run owns the resume date (a stopped dry run must not
        // make the next Seed start mid-window).
        if (!dry) setSeedFrom(cursor || '');
        setSeedProgress({ ...totals, cursor, elapsedMs: Date.now() - t0 });
        if (done) break;
        if (!cursor || cursor === sent) throw new Error(`Server returned done=false without progress (nextDate=${res.nextDate})`);
        if (stopRef.current) { stopped = true; break; }
      }
      setSeedResult({ mode, ...totals, days, first, last, ref, done, stopped, nextDate: cursor, elapsedMs: Date.now() - t0 });
    } catch (error) {
      setSeedResult({ mode, ...totals, days, first, last, ref, done: false, stopped: false, nextDate: cursor, elapsedMs: Date.now() - t0, error: error.message });
    } finally {
      setSeedMode(null);
      setSeedProgress(null);
      setStopRequested(false);
    }
  };
  const renderSeedResult = () => {
    if (!seedResult) return null;
    const r = seedResult;
    const skippedText = Object.keys(r.skipped || {}).sort().map((k) => `${k} ${fmtNum(r.skipped[k])}`).join(', ');
    const range = r.first ? ` (${r.first} → ${r.last || r.first})` : '';
    const summary = `${r.mode === 'dry' ? 'Dry run' : 'Seeded'}: ${fmtNum(r.processed)} day(s)${range} in ${fmtElapsed(r.elapsedMs)} over ${r.calls} call(s)` +
      ` — ${fmtNum(r.scanned)} bookings scanned, counted ${fmtNum(r.n)} requests · ${fmtNum(r.h)} hails · ${fmtNum(r.u)} unmet` +
      (skippedText ? `; skipped ${skippedText}` : '') +
      (r.mode === 'dry' ? '. Nothing written.' : ` · ${fmtNum(r.written)} day node(s) written, ${fmtNum(r.cleared)} cleared` + (r.ref !== null ? ` · reference ${r.ref} req/h.` : '.'));
    const resume = r.nextDate
      ? (r.mode === 'dry' ? ` Stopped at ${r.nextDate}.` : ` Resume from ${r.nextDate} is filled in — press Seed again to continue.`)
      : '';
    if (r.error) return <InlineMessage kind="error" onClose={() => setSeedResult(null)}>{r.error} — partial: {summary}{resume}</InlineMessage>;
    return (
      <InlineMessage kind={r.stopped ? 'warning' : 'success'} onClose={() => setSeedResult(null)}>
        {r.stopped ? `Stopped.${resume} ` : ''}{summary}
        {r.mode !== 'dry' && r.done ? ' Drivers see the baseline on the next publish (≤ 10 min) once step 2 is on.' : ''}
      </InlineMessage>
    );
  };

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  const stopButton = (
    <Button
      variant="outlined"
      color="neutral"
      startIcon={<StopIcon />}
      disabled={stopRequested}
      onClick={requestStop}
      sx={nowrapSx}
    >
      {stopRequested ? 'Stopping after current call…' : 'Stop'}
    </Button>
  );

  // ── Rollups result rendering ────────────────────────────────────────
  const renderRollupsResult = () => {
    if (!rollupsResult) return null;
    const { mode, months, calls, elapsedMs, nextMonth, stopped, error } = rollupsResult;
    const keys = Object.keys(months || {}).sort();
    const resumeHint = nextMonth ? ` Resume by setting From to ${nextMonth}.` : '';
    if (error && keys.length === 0) {
      return <InlineMessage kind="error" onClose={() => setRollupsResult(null)}>{error}{resumeHint}</InlineMessage>;
    }
    if (mode === 'dry') {
      const rows = keys.map((mk) => ({ mk, ...summariseParityMonth(months[mk]) }));
      const flaggedCount = rows.filter((r) => r.flagged).length;
      const driverMismatchTotal = rows.reduce((s, r) => s + r.driverMismatch, 0);
      const fleetMismatchTotal = rows.reduce((s, r) => s + r.fleetMismatch, 0);
      const summary = `Parity check: ${keys.length} month(s) in ${fmtElapsed(elapsedMs)} over ${calls} call(s) — ` +
        `${flaggedCount} flagged, ${fmtNum(driverMismatchTotal)} driver row(s) and ${fmtNum(fleetMismatchTotal)} fleet row(s) differ. Nothing written.`;
      let severity = flaggedCount > 0 || driverMismatchTotal > 0 || fleetMismatchTotal > 0 ? 'warning' : 'success';
      if (error) severity = 'error';
      return (
        <Box>
          <InlineMessage kind={severity} sx={{ mb: 1.5 }} onClose={() => setRollupsResult(null)}>
            {error ? `${error} — partial result below.${resumeHint} ` : (stopped ? `Stopped.${resumeHint} ` : '')}{summary}
          </InlineMessage>
          <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}><strong>Month</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Trips computed / stored</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Max |Δ| money</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Driver rows ≠</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Fleet rows ≠</strong></TableCell>
                  <TableCell sx={headSx}><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.mk} hover>
                    <TableCell sx={cellSx}>{r.mk}</TableCell>
                    <TableCell sx={cellSx} align="right">{fmtNum(r.ridesComputed)} / {fmtNum(r.ridesStored)}</TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Tooltip title={r.worstField ? `field: ${r.worstField}` : 'no money drift'}>
                        <span>{fmtMoney(r.maxMoneyDiff)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Tooltip title={`rows computed ${fmtNum(r.driverRows.computed)} / stored ${fmtNum(r.driverRows.stored)}`}>
                        <Box component="span" sx={{ color: r.driverMismatch > 0 ? c.WARNING_TEXT : undefined, fontWeight: r.driverMismatch > 0 ? 700 : 400 }}>{fmtNum(r.driverMismatch)}</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Tooltip title={`rows computed ${fmtNum(r.fleetRows.computed)} / stored ${fmtNum(r.fleetRows.stored)}`}>
                        <Box component="span" sx={{ color: r.fleetMismatch > 0 ? c.WARNING_TEXT : undefined, fontWeight: r.fleetMismatch > 0 ? 700 : 400 }}>{fmtNum(r.fleetMismatch)}</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Tooltip title={`tolerance ±${fmtMoney(r.tolerance)} = max(1, 1% of ${fmtNum(r.ridesComputed)} trips)`}>
                        <span><StatusChip tone={r.flagged ? 'error' : 'success'} label={r.flagged ? 'DRIFT' : 'OK'} /></span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }
    // Write mode — months[mk] is the scan stats of the month just rebuilt.
    const summary = `Rebuilt ${keys.length} month(s)${keys.length ? ` (${keys[0]} → ${keys[keys.length - 1]})` : ''} in ${fmtElapsed(elapsedMs)} over ${calls} call(s).`;
    return (
      <Box>
        <InlineMessage kind={error ? 'error' : (stopped ? 'warning' : 'success')} sx={{ mb: 1.5 }} onClose={() => setRollupsResult(null)}>
          {error ? `${error} — ${summary}${resumeHint}` : (stopped ? `Stopped. ${summary}${resumeHint}` : summary)}
        </InlineMessage>
        {keys.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}><strong>Month</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Scanned</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Counted</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>From archive</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Re-bucketed</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>Unparseable</strong></TableCell>
                  <TableCell sx={headSx} align="right"><strong>No tripdate</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((mk) => {
                  const s = months[mk] || {};
                  return (
                    <TableRow key={mk} hover>
                      <TableCell sx={cellSx}>{mk}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtNum(s.scanned)}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtNum(s.counted)}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtNum(s.archived)}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtNum(s.rebucketed)}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtNum(s.unparseable)}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtNum(s.noTripdate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // ── tripdateMs result rendering ─────────────────────────────────────
  const renderTripdateResult = () => {
    if (!tripdateResult) return null;
    const r = tripdateResult;
    const stampLabel = r.mode === 'dry' ? 'would stamp' : 'stamped';
    const counts = `${r.calls} call(s) in ${fmtElapsed(r.elapsedMs)}: scanned ${fmtNum(r.scanned)}, ${stampLabel} ${fmtNum(r.stamped)}, ` +
      `skipped ${fmtNum(r.skipped)} (already stamped), pending-skipped ${fmtNum(r.skippedPending)}, unparseable ${fmtNum(r.unparseable)}` +
      (r.mode === 'dry' ? '.' : `, ${fmtNum(r.flushes)} flush(es).`);
    const resume = r.nextCursor ? ` Resume cursor prefilled: ${r.nextCursor}` : '';
    if (r.error) {
      return <InlineMessage kind="error" onClose={() => setTripdateResult(null)}>{r.error} — {counts}{resume}</InlineMessage>;
    }
    if (r.stopped) {
      return <InlineMessage kind="warning" onClose={() => setTripdateResult(null)}>Stopped after {counts}{resume}</InlineMessage>;
    }
    return (
      <InlineMessage kind="success" onClose={() => setTripdateResult(null)}>
        Whole /bookings tree scanned{r.mode === 'dry' ? ' (dry run, nothing written)' : ''} — {counts}
      </InlineMessage>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineMessage kind="info">
        Deploy rules → functions before running; run backfills before flipping useReportRollups / useTripdateMsIndex in Settings.
      </InlineMessage>

      {rebuildResult && (
        <InlineMessage kind={rebuildResult.success ? 'success' : 'error'} onClose={() => setRebuildResult(null)}>
          {rebuildResult.success ? rebuildResult.message : rebuildResult.error}
        </InlineMessage>
      )}
      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, color: c.INK, marginInlineEnd: 'auto' }}>One-shot tools</Typography>
        <Button
          variant="outlined"
          color="neutral"
          startIcon={enrolling ? <CircularProgress size={16} color="inherit" /> : <EventAvailableIcon />}
          disabled={busy}
          onClick={handleBulkFlatRateEnroll}
          sx={nowrapSx}
        >
          {enrolling
            ? (enrollProgress ? `Switched ${enrollProgress.done}/${enrollProgress.total}…` : 'Switching…')
            : `Flat-Rate All (app ${MIN_FLAT_RATE_APP_VERSION}+)`}
        </Button>
        <Button
          variant="outlined"
          color="neutral"
          startIcon={<DirectionsCarIcon />}
          disabled={busy}
          onClick={() => setPlaceholderOpen(true)}
          sx={nowrapSx}
        >
          Fill Vehicle Placeholders
        </Button>
        <Button
          variant="outlined"
          color="neutral"
          startIcon={rebuilding ? <CircularProgress size={16} color="inherit" /> : <StorageIcon />}
          disabled={busy}
          onClick={handleRebuildCustomerIndex}
          sx={nowrapSx}
        >
          {rebuilding ? 'Rebuilding…' : 'Rebuild Customer Index'}
        </Button>
      </Paper>

      <Box display="flex" flexDirection="column" gap={2}>
        {/* Earnings rollups */}
        <SectionCard id="mt-rollups" icon={<AssessmentOutlinedIcon />} hue="success" title="Earnings rollups (report_rollups)">
            <Typography variant="body2" sx={descSx}>
              Rebuilds /report_rollups (system / driver / fleet monthly) from the bookings tree (hot + archive), one Addis month per step.
              Dry-run compares computed vs stored and writes nothing. Months before {ROLLUP_MIN_MONTH} are never rebuilt; each call runs
              at most {ROLLUPS_MAX_SECONDS} s and the tab resumes from nextMonth until done. Stay on this tab while a run is in progress.
            </Typography>
            <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap" mb={2}>
              <LabelledField
                id="mt-from-month"
                label="From (YYYY-MM)"
                value={fromMonth}
                onChange={(e) => setFromMonth(e.target.value)}
                disabled={busy}
                sx={{ width: 150 }}
              />
              <LabelledField
                id="mt-to-month"
                label="To (YYYY-MM)"
                value={toMonth}
                onChange={(e) => setToMonth(e.target.value)}
                disabled={busy}
                sx={{ width: 150 }}
              />
              <Button
                variant="outlined"
                color="neutral"
                startIcon={rollupsMode === 'dry' ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                disabled={busy}
                onClick={() => runRollups(true)}
                sx={nowrapSx}
              >
                {rollupsMode === 'dry' ? 'Checking…' : 'Dry-run (parity check)'}
              </Button>
              <Button
                variant="contained"
                startIcon={rollupsMode === 'run' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                disabled={busy}
                onClick={() => runRollups(false)}
                sx={nowrapSx}
              >
                {rollupsMode === 'run' ? 'Rebuilding…' : 'Run (rebuild months)'}
              </Button>
              {rollupsMode !== null && stopButton}
            </Box>
            {rollupsProgress && (
              <Typography sx={progressSx}>
                {`${rollupsMode === 'dry' ? 'Parity check' : 'Rebuild'} · call ${rollupsProgress.calls} · months ${rollupsProgress.months}` +
                  ` · last ${rollupsProgress.lastMonth || '—'} · elapsed ${fmtElapsed(rollupsProgress.elapsedMs)}` +
                  (rollupsProgress.calls === 0 ? ' · waiting for first response…' : '')}
              </Typography>
            )}
            {renderRollupsResult()}
        </SectionCard>

        {/* tripdateMs stamping */}
        <SectionCard id="mt-tripdate" icon={<EventNoteOutlinedIcon />} hue="info" title="Booking tripdateMs stamping">
            <Typography variant="body2" sx={descSx}>
              Stamps the numeric tripdateMs mirror on every /bookings row (throttled: 100 paths per flush, ~1.5 s pause) so the portal can
              query one typed index. Dry-run only counts. Resumable by booking key: each call runs at most {TRIPDATE_MAX_SECONDS} s and the
              tab continues from nextCursor until done; the cursor below is kept in this browser so a stopped run can be resumed later.
            </Typography>
            <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap" mb={2}>
              <LabelledField
                id="mt-cursor"
                label="Resume from cursor (booking key; blank = start)"
                value={cursorField}
                onChange={(e) => setCursorField(e.target.value)}
                disabled={busy}
                sx={{ minWidth: { xs: '100%', sm: 340 }, flex: 1 }}
                inputProps={{ spellCheck: false, autoComplete: 'off' }}
              />
              <Button
                variant="outlined"
                color="neutral"
                startIcon={tripdateMode === 'dry' ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                disabled={busy}
                onClick={() => runTripdate(true)}
                sx={nowrapSx}
              >
                {tripdateMode === 'dry' ? 'Counting…' : 'Dry-run (count only)'}
              </Button>
              <Button
                variant="contained"
                startIcon={tripdateMode === 'run' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                disabled={busy}
                onClick={() => runTripdate(false)}
                sx={nowrapSx}
              >
                {tripdateMode === 'run' ? 'Stamping…' : 'Run (stamp)'}
              </Button>
              {tripdateMode !== null && stopButton}
            </Box>
            {tripdateProgress && (
              <Typography sx={progressSx}>
                {`${tripdateMode === 'dry' ? 'Count' : 'Stamp'} · calls ${tripdateProgress.calls} · scanned ${fmtNum(tripdateProgress.scanned)}` +
                  ` · ${tripdateMode === 'dry' ? 'would stamp' : 'stamped'} ${fmtNum(tripdateProgress.stamped)} · skipped ${fmtNum(tripdateProgress.skipped)}` +
                  ` · pending-skipped ${fmtNum(tripdateProgress.skippedPending)} · unparseable ${fmtNum(tripdateProgress.unparseable)}` +
                  ` · cursor ${shortKey(tripdateProgress.cursor)} · elapsed ${fmtElapsed(tripdateProgress.elapsedMs)}` +
                  (tripdateProgress.calls === 0 ? ' · waiting for first response…' : '')}
              </Typography>
            )}
            {renderTripdateResult()}
        </SectionCard>

        {/* Hot zones demand-map history seed */}
        <SectionCard id="mt-seed" icon={<MapOutlinedIcon />} hue="accent" title="Demand map history seed (Hot zones)">
            <Typography variant="body2" sx={descSx}>
              Backfills the weekday/hour demand baseline from past bookings so the driver Hot zones map is useful on day one.
              Reads one Addis day of /bookings per query via the tripdateMs index (~1 MB per day today), overwrites that day under
              /demand_cells/hist (idempotent, today is never seeded) and recomputes the colour reference at the end. Each call runs at
              most {DEMAND_SEED_MAX_SECONDS} s and the tab continues from the server&apos;s nextDate until done. Dry-run counts only.
              Run it after switching on <strong>step 1</strong> (count booking requests) in Settings › General, and again if you raise
              demandMapBaselineWeeks.
            </Typography>
            {settingsData?.settings?.demandHeatIngestEnabled !== true && (
              <InlineMessage kind="info" sx={{ mb: 2 }}>
                Step 1 (demandHeatIngestEnabled) is OFF: the seed still works, but new requests are not being counted until it is switched on.
              </InlineMessage>
            )}
            <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap" mb={2}>
              <LabelledField
                id="mt-seed-from"
                label="Resume from (YYYY-MM-DD; blank = whole window)"
                value={seedFrom}
                onChange={(e) => setSeedFrom(e.target.value)}
                disabled={busy}
                sx={{ width: { xs: '100%', sm: 300 } }}
                inputProps={{ spellCheck: false, autoComplete: 'off' }}
              />
              <LabelledField
                id="mt-seed-days"
                label={`Days of history (1–${DEMAND_SEED_MAX_DAYS})`}
                value={seedDays}
                onChange={(e) => setSeedDays(e.target.value)}
                onBlur={() => setSeedDays(String(seedDaysNumber()))}
                disabled={busy}
                inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                sx={{ width: 220 }}
              />
              <Button
                variant="outlined"
                color="neutral"
                startIcon={seedMode === 'dry' ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                disabled={busy}
                onClick={() => runDemandSeed(true)}
                sx={nowrapSx}
              >
                {seedMode === 'dry' ? 'Counting…' : 'Dry-run (count only)'}
              </Button>
              <Button
                variant="contained"
                startIcon={seedMode === 'run' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                disabled={busy}
                onClick={() => runDemandSeed(false)}
                sx={nowrapSx}
              >
                {seedMode === 'run' ? 'Seeding…' : `Seed ${seedDaysNumber()} days`}
              </Button>
              {seedMode !== null && stopButton}
            </Box>
            {seedProgress && (
              <Typography sx={progressSx}>
                {`${seedMode === 'dry' ? 'Count' : 'Seed'} · calls ${seedProgress.calls} · days ${fmtNum(seedProgress.processed)} · scanned ${fmtNum(seedProgress.scanned)}` +
                  ` · requests ${fmtNum(seedProgress.n)} · hails ${fmtNum(seedProgress.h)} · unmet ${fmtNum(seedProgress.u)}` +
                  ` · next ${seedProgress.cursor || '—'} · elapsed ${fmtElapsed(seedProgress.elapsedMs)}` +
                  (seedProgress.calls === 0 ? ' · waiting for first response…' : '')}
              </Typography>
            )}
            {renderSeedResult()}
        </SectionCard>
      </Box>

      <VehiclePlaceholderDialog
        open={placeholderOpen}
        onClose={() => setPlaceholderOpen(false)}
        onDone={setRebuildResult}
      />
    </Box>
  );
}
